// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { Alert, Spinner } from '@loa/ui-components';
import {
  financialPositionApi,
  type TaxClassification,
  type TaxClassificationClientFlags,
  type TaxClassificationItem,
} from '../../api/financialPositionApi';

interface Props {
  profileId: string;
}

/** Selectable tax treatments + friendly labels (cashflow-tax-modeling-prd.md §7.7). */
const TAX_TREATMENTS: Array<{ value: string; label: string }> = [
  { value: 'CGT', label: 'Capital Gains Tax' },
  { value: 'EXIT_TAX', label: 'Exit tax (41%, 8-yr deemed disposal)' },
  { value: 'DIRT', label: 'Deposit interest (DIRT)' },
  { value: 'PPR_EXEMPT', label: 'Principal private residence (exempt)' },
  { value: 'PENSION', label: 'Pension' },
  { value: 'BUSINESS_RELIEF', label: 'Business relief' },
];

const TREATMENT_LABEL: Record<string, string> = Object.fromEntries(
  TAX_TREATMENTS.map((t) => [t.value, t.label]),
);

function treatmentLabel(value: string): string {
  return TREATMENT_LABEL[value] ?? value;
}

const KIND_GROUPS: Array<{ kind: TaxClassificationItem['kind']; title: string }> = [
  { kind: 'ASSET', title: 'Assets' },
  { kind: 'INVESTMENT', title: 'Investments' },
  { kind: 'HOLDING', title: 'Holdings' },
];

/**
 * §7.7 "Tax classification" — advisor panel to set the tax treatment of each
 * taxable item (assets / investments / holdings) and the household tax-profile
 * flags per client. Read-only PPR display; treatment select is the editable
 * control. Mounted on the relationship Financial Position tab beneath the
 * dashboard. See docs/specs/cashflow-tax-modeling-prd.md §7.7.
 */
export function TaxClassificationPanel({ profileId }: Props) {
  const [data, setData] = useState<TaxClassification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    financialPositionApi
      .getTaxClassification(profileId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the tax classification.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [profileId]);

  if (loading) return <Spinner />;
  if (error) return <Alert variant="error">{error}</Alert>;
  if (!data) return null;

  const patchItem = (id: string, effectiveTreatment: string, overridden: boolean) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            items: prev.items.map((it) =>
              it.id === id ? { ...it, effectiveTreatment, overridden } : it,
            ),
          }
        : prev,
    );
  };

  const patchClient = (clientId: string, flags: TaxClassificationClientFlags) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            clients: prev.clients.map((c) => (c.clientId === clientId ? flags : c)),
          }
        : prev,
    );
  };

  return (
    <div data-testid="tax-classification-panel" className="flex flex-col gap-lg">
      <HouseholdTaxProfile
        profileId={profileId}
        clients={data.clients}
        onClientSaved={patchClient}
      />
      <ItemsSection profileId={profileId} items={data.items} onItemSaved={patchItem} />
    </div>
  );
}

/** Per-client tax-profile flags card with a per-client Save. */
function HouseholdTaxProfile({
  profileId,
  clients,
  onClientSaved,
}: {
  profileId: string;
  clients: TaxClassificationClientFlags[];
  onClientSaved: (clientId: string, flags: TaxClassificationClientFlags) => void;
}) {
  return (
    <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] p-lg gap-md">
      <div className="text-black text-body-01 font-medium leading-normal font-sans">
        Household tax profile
      </div>
      {clients.length === 0 ? (
        <div className="text-body-03 text-grey-04">No clients in this relationship.</div>
      ) : (
        <div className="flex flex-col gap-md">
          {clients.map((c) => (
            <ClientTaxCard
              key={c.clientId}
              profileId={profileId}
              client={c}
              onSaved={onClientSaved}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const FLAG_FIELDS: Array<{ key: keyof Pick<
  TaxClassificationClientFlags,
  'medicalCard' | 'selfEmployed' | 'homeCarer' | 'singlePersonChildCarer'
>; label: string }> = [
  { key: 'medicalCard', label: 'Medical card' },
  { key: 'selfEmployed', label: 'Self-employed' },
  { key: 'homeCarer', label: 'Home carer' },
  { key: 'singlePersonChildCarer', label: 'Single-person child carer' },
];

const COUNT_FIELDS: Array<{ key: keyof Pick<
  TaxClassificationClientFlags,
  'incapacitatedChildrenCount' | 'dependentRelativesCount'
>; label: string }> = [
  { key: 'incapacitatedChildrenCount', label: 'Incapacitated children' },
  { key: 'dependentRelativesCount', label: 'Dependent relatives' },
];

function ClientTaxCard({
  profileId,
  client,
  onSaved,
}: {
  profileId: string;
  client: TaxClassificationClientFlags;
  onSaved: (clientId: string, flags: TaxClassificationClientFlags) => void;
}) {
  const [form, setForm] = useState<TaxClassificationClientFlags>(client);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setBool = (key: (typeof FLAG_FIELDS)[number]['key'], value: boolean) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };
  const setCount = (key: (typeof COUNT_FIELDS)[number]['key'], value: number) => {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  };

  const save = () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    financialPositionApi
      .setClientTaxProfile(profileId, client.clientId, {
        medicalCard: form.medicalCard,
        selfEmployed: form.selfEmployed,
        homeCarer: form.homeCarer,
        singlePersonChildCarer: form.singlePersonChildCarer,
        incapacitatedChildrenCount: form.incapacitatedChildrenCount,
        dependentRelativesCount: form.dependentRelativesCount,
      })
      .then(() => {
        onSaved(client.clientId, form);
        setSaved(true);
      })
      .catch(() => setError('Could not save the tax profile.'))
      .finally(() => setSaving(false));
  };

  return (
    <div
      data-testid={`tax-client-${client.clientId}`}
      className="rounded-button border border-[#E1E1E1] p-md flex flex-col gap-[12px]"
    >
      <div className="flex items-center justify-between">
        <div className="text-body-02 font-medium text-black">{client.name}</div>
        {saved && (
          <span data-testid={`tax-client-saved-${client.clientId}`} className="text-[13px] text-[#1E8E4E]">
            ✓ Saved
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-lg gap-y-[10px]">
        {FLAG_FIELDS.map((f) => (
          <label key={f.key} className="flex items-center gap-sm text-body-03 text-black">
            <input
              type="checkbox"
              data-testid={`tax-client-${client.clientId}-${f.key}`}
              checked={form[f.key]}
              onChange={(e) => setBool(f.key, e.target.checked)}
            />
            {f.label}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-x-lg gap-y-[10px]">
        {COUNT_FIELDS.map((f) => (
          <label key={f.key} className="flex items-center gap-sm text-body-03 text-black">
            {f.label}
            <input
              type="number"
              min={0}
              data-testid={`tax-client-${client.clientId}-${f.key}`}
              value={form[f.key]}
              onChange={(e) => setCount(f.key, Math.max(0, Math.floor(Number(e.target.value) || 0)))}
              className="w-[72px] rounded-lg border-[1.5px] border-[#BDB8FF] px-sm py-xs"
            />
          </label>
        ))}
      </div>
      {error && <Alert variant="error">{error}</Alert>}
      <div className="flex justify-end">
        <button
          type="button"
          data-testid={`tax-client-save-${client.clientId}`}
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-purple-primary px-[12px] py-[6px] text-body-03 text-white disabled:opacity-60"
        >
          Save
        </button>
      </div>
    </div>
  );
}

/** Taxable-items table grouped by kind, with an editable treatment select. */
function ItemsSection({
  profileId,
  items,
  onItemSaved,
}: {
  profileId: string;
  items: TaxClassificationItem[];
  onItemSaved: (id: string, effectiveTreatment: string, overridden: boolean) => void;
}) {
  return (
    <div className="flex flex-col w-full rounded-card bg-white shadow-[0_0_10px_0_rgba(0,0,0,0.10)] p-lg gap-md">
      <div className="text-black text-body-01 font-medium leading-normal font-sans">
        Tax classification
      </div>
      {items.length === 0 ? (
        <div className="text-body-03 text-grey-04">No taxable items for this relationship yet.</div>
      ) : (
        KIND_GROUPS.map((g) => {
          const groupItems = items.filter((it) => it.kind === g.kind);
          if (groupItems.length === 0) return null;
          return (
            <div key={g.kind} className="flex flex-col gap-sm" data-testid={`tax-group-${g.kind}`}>
              <div className="text-body-02 font-medium text-black">{g.title}</div>
              <table className="w-full border-collapse text-body-03">
                <thead>
                  <tr className="text-left text-grey-04">
                    <th className="py-sm pr-sm font-medium">Item</th>
                    <th className="py-sm px-sm font-medium">Owner</th>
                    <th className="py-sm px-sm font-medium">Type</th>
                    {g.kind === 'ASSET' && (
                      <th className="py-sm px-sm font-medium">PPR</th>
                    )}
                    <th className="py-sm pl-sm font-medium">Tax treatment</th>
                  </tr>
                </thead>
                <tbody>
                  {groupItems.map((it) => (
                    <ItemRow
                      key={it.id}
                      profileId={profileId}
                      item={it}
                      onSaved={onItemSaved}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}

function ItemRow({
  profileId,
  item,
  onSaved,
}: {
  profileId: string;
  item: TaxClassificationItem;
  onSaved: (id: string, effectiveTreatment: string, overridden: boolean) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const apply = (treatment: string | null) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    financialPositionApi
      .setTaxTreatment(profileId, item.kind, item.id, treatment)
      .then((effective) => {
        // A null/'' treatment clears the override; otherwise it's overridden.
        onSaved(item.id, effective, treatment != null && treatment !== '');
      })
      .catch(() => setError('Could not update the tax treatment.'))
      .finally(() => setSaving(false));
  };

  // Ensure the current effective treatment is selectable even if not in the known list.
  const knownValues = TAX_TREATMENTS.map((t) => t.value);
  const extra = knownValues.includes(item.effectiveTreatment) ? [] : [item.effectiveTreatment];

  return (
    <tr className="border-t border-[#E1E1E1] align-top">
      <td className="py-[10px] pr-sm text-black">{item.label}</td>
      <td className="py-[10px] px-sm text-grey-04">{item.owner}</td>
      <td className="py-[10px] px-sm text-grey-04">{item.itemType}</td>
      {item.kind === 'ASSET' && (
        <td className="py-[10px] px-sm text-grey-04">
          <input
            type="checkbox"
            data-testid={`tax-item-ppr-${item.id}`}
            checked={item.principalPrivateResidence === true}
            readOnly
            disabled
            aria-label={`Principal private residence for ${item.label} (read-only)`}
          />
        </td>
      )}
      <td className="py-[10px] pl-sm">
        <div className="flex flex-col gap-[6px]">
          <div className="flex items-center gap-sm">
            <select
              data-testid={`tax-item-treatment-${item.id}`}
              value={item.effectiveTreatment}
              disabled={saving}
              onChange={(e) => apply(e.target.value)}
              className="rounded-lg border-[1.5px] border-[#BDB8FF] px-[10px] py-[6px] text-body-03 disabled:opacity-60"
            >
              {extra.map((v) => (
                <option key={v} value={v}>
                  {treatmentLabel(v)}
                </option>
              ))}
              {TAX_TREATMENTS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {item.overridden && (
              <span
                data-testid={`tax-item-override-chip-${item.id}`}
                className="inline-block rounded-[6px] bg-[#FDF1E3] px-[6px] py-[1px] text-[11px] font-medium text-[#B9770E]"
              >
                override
              </span>
            )}
            {item.overridden && (
              <button
                type="button"
                data-testid={`tax-item-reset-${item.id}`}
                onClick={() => apply(null)}
                disabled={saving}
                className="text-[13px] text-purple-primary hover:underline disabled:opacity-60"
              >
                Reset to default
              </button>
            )}
          </div>
          {error && <span className="text-body-04 text-[#C0392B]">{error}</span>}
        </div>
      </td>
    </tr>
  );
}
